'use client'

import { useTurnstile } from '@/Turnstile'
import {
  clearCloudflareChallengeWithTurnstile,
  isCloudflareChallengeResponse,
} from '@/lib/cloudflarePreclearance'

export interface UseCaptchaOptions {
  enabled?: boolean
  endpoint?: string
}

export interface UseCaptchaReturn {
  verifyCaptcha: () => Promise<boolean>
  isEnabled: boolean
}

/**
 * Custom hook for bot verification.
 * Centralizes Turnstile logic to eliminate code duplication.
 */
export const useCaptcha = ({
  enabled = true,
  endpoint = '/api/verifycaptcha',
}: UseCaptchaOptions = {}): UseCaptchaReturn => {
  const { executeTurnstile } = useTurnstile()

  const verifyToken = async (token: string) => {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  }

  const verifyCaptcha = async (): Promise<boolean> => {
    if (!enabled) {
      return true
    }

    try {
      const token = await executeTurnstile()
      let response = await verifyToken(token)

      if (isCloudflareChallengeResponse(response)) {
        await clearCloudflareChallengeWithTurnstile()
        response = await verifyToken(await executeTurnstile())
      }

      if (!response.ok) {
        throw new Error('Verification failed. Please try again.')
      }

      return true
    } catch (error) {
      // Re-throw error to be handled by the calling component
      throw error
    }
  }

  return {
    verifyCaptcha,
    isEnabled: enabled,
  }
}
